// Fallback for when a Google account carries no display name. Lossy by nature —
// "anna.mueller" cannot become "Anna Müller" — which is why the profile name
// takes precedence over this.

const PARTICLES = new Set(["von", "van", "de", "da", "di", "del", "della", "zu"]);

function titleCase(token: string): string {
  return token
    .split("-")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : p))
    .join("-");
}

export function nameFromEmail(email: string): string {
  const local = (email ?? "").split("@")[0] ?? "";
  const tokens = local.split(".").map((t) => t.trim()).filter(Boolean);
  return tokens
    .map((token, i) =>
      i > 0 && PARTICLES.has(token.toLowerCase()) ? token.toLowerCase() : titleCase(token))
    .join(" ");
}

// --- Account-scoped name override --------------------------------------
//
// The override a person types in Settings must only ever apply to the
// account that set it. It is stored as JSON — `{ email, name }` — rather
// than a bare string, so a session can check "is this mine?" before using
// it. Sessions ending without an explicit sign-out (a disabled Workspace
// account, a revoked token, a failed refresh) leave the raw value sitting in
// localStorage; scoping it this way means the next person to sign in on the
// same device simply doesn't match, rather than silently inheriting it.

export interface NameOverride {
  email: string;
  name: string;
}

/**
 * Parse the raw localStorage value for the name override. Anything that
 * isn't the current `{ email, name }` JSON shape — including an override
 * written under the old unscoped bare-string format, or corrupt JSON — is
 * treated as absent. Guessing an owner for a legacy value would risk
 * misattributing it, so "no override" is the only safe reading.
 */
export function parseNameOverride(raw: string | null): NameOverride | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed && typeof parsed === "object" &&
      typeof (parsed as NameOverride).email === "string" &&
      typeof (parsed as NameOverride).name === "string" &&
      (parsed as NameOverride).email.trim() &&
      (parsed as NameOverride).name.trim()
    ) {
      return { email: (parsed as NameOverride).email, name: (parsed as NameOverride).name };
    }
  } catch {
    // Legacy bare-string override, or corrupt JSON — treat as absent.
  }
  return null;
}

/**
 * The override name to use, if the raw stored value belongs to
 * `currentEmail`. Returns null for "no override" — no stored value, a
 * legacy/unrecognised format, or an override that belongs to someone else.
 */
export function resolveOverrideName(raw: string | null, currentEmail: string): string | null {
  const override = parseNameOverride(raw);
  if (!override) return null;
  if (override.email.toLowerCase() !== currentEmail.toLowerCase()) return null;
  const trimmed = override.name.trim();
  return trimmed.length > 0 ? trimmed : null;
}
